import React, {ChangeEvent, DragEvent, useEffect, useRef, useState} from "react";
import {Alert, Button, Image} from "react-bootstrap";
import {url} from "../../utils/UrlsUtil.ts";
import "../../../style/common/components/image/_image_uploader.scss";
import {useLoading} from "../loading/LoadingProvider.tsx";

interface ResizeImageUploaderProps{
    min_width?:number;
    min_height?:number;
    resize_width:number;
    remove_exif?: boolean;
    selectedFile: Blob | null;
    showDelete?: boolean;
    handleSelectedImage?: (imgBlob: Blob | null) => void;
}

const ResizeImageUploader:React.FC<ResizeImageUploaderProps> = (
    {
        min_width = 0,
        min_height = 0,
        resize_width = 1,
        remove_exif = false,
        selectedFile = null,
        showDelete = false,
        handleSelectedImage = () => {},
    }
) => {
    const { showLoading, hideLoading } = useLoading();
    const [image, setImage] = useState<string | null>(null);
    const [error, setError] = useState<string>('');
    const fileInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        if (selectedFile === null) {
            clear();
        } else {
            const imageUrl = URL.createObjectURL(selectedFile);
            setImage(imageUrl);
        }
    }, [selectedFile]);
    const handleFileChange = (event: ChangeEvent<HTMLInputElement>): void => {
        const file = event.target.files?.[0];
        validSizeAndResize(file);
    };

    const handleDragOver = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
        event.preventDefault();
        const file = event.dataTransfer.files?.[0];
        validSizeAndResize(file);
    };

    const validSizeAndResize = (file: File | undefined): void => {
        setError('');
        setImage(null);

        if (!file) {
            setError("画像ファイルを選択してください");
            if (handleSelectedImage) {
                handleSelectedImage(null);
            }
            return;
        }

        if (!file.type.startsWith('image/')) {
            setError("画像ファイルを選択してください");
            if (handleSelectedImage) {
                handleSelectedImage(null);
            }
            return;
        }

        const reader = new FileReader();
        reader.onloadend = () => {
            const img = new window.Image();
            img.src = reader.result as string;

            img.onload = () => {
                if (img.width < min_width || img.height < min_height) {
                    setError(`${min_width}x${min_height}px以上画像を選択してください. 選択した画像サイズ: ${img.width}x${img.height}px`);
                    setImage(null);
                    if (handleSelectedImage) {
                        handleSelectedImage(null);
                    }
                } else {
                    showLoading();
                    resizeImage(file).finally(()=> {hideLoading()});
                }
            };
            img.onerror = () => {
                setError("画像ファイルが読み込むことができません。");
                if (handleSelectedImage) {
                    handleSelectedImage(null);
                }
            };
        };
        reader.readAsDataURL(file);
    };

    const handleClick = (): void => {
        fileInputRef.current?.click();
    };
    const onClickClear = (): void =>{
        clear();
        handleSelectedImage(null);
    }
    const clear = () => {
        setError('');
        setImage(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }
    const resizeImage = async (uploadFile: File) => {
        try{
            const formData = new FormData();
            formData.append('file', uploadFile);
            formData.append('width', resize_width.toString());
            formData.append('remove_exif_flag', remove_exif? "1" : "0");
            const response = await fetch(
                url("/api/image/resize"),
                {
                    method: "POST",
                    body: formData
                });
            if(!response.ok){
                // Xử lý lỗi nếu phản hồi không thành công
                setError("画像ファイルがアップロードできません");
                setImage(null);
            }else{
                // Nhận phản hồi dưới dạng Blob
                const imageBlob = await response.blob();
                if (imageBlob) {
                    const imageUrl = URL.createObjectURL(imageBlob);
                    setImage(imageUrl);
                    handleSelectedImage(imageBlob);
                }
            }
        }catch (err){
            setError("画像ファイルがアップロードできません");
            setImage(null);
        }
    };
    return (
        <div className="image-uploader-component">
            {showDelete && <div className="image-uploader-title">
                <Button variant="danger" onClick={onClickClear}>
                    クリア
                    <span className="btn-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-trash3-fill" viewBox="0 0 16 16">
                        <path d="M11 1.5v1h3.5a.5.5 0 0 1 0 1h-.538l-.853 10.66A2 2 0 0 1 11.115 16h-6.23a2 2 0 0 1-1.994-1.84L2.038 3.5H1.5a.5.5 0 0 1 0-1H5v-1A1.5 1.5 0 0 1 6.5 0h3A1.5 1.5 0 0 1 11 1.5m-5 0v1h4v-1a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 0-.5.5M4.5 5.029l.5 8.5a.5.5 0 1 0 .998-.06l-.5-8.5a.5.5 0 1 0-.998.06m6.53-.528a.5.5 0 0 0-.528.47l-.5 8.5a.5.5 0 0 0 .998.058l.5-8.5a.5.5 0 0 0-.47-.528M8 4.5a.5.5 0 0 0-.5.5v8.5a.5.5 0 0 0 1 0V5a.5.5 0 0 0-.5-.5"/>
                    </svg>
                </span>
                </Button>
            </div>}
            <div className="image-uploader-body"
                 onDragOver={handleDragOver}
                 onDrop={handleDrop}
                 onClick={handleClick}
            >
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                />
                {image ? (
                    <Image src={image} fluid />
                ) : (
                    <>
                        <p className="text-muted">
                            <span className="pc">画像をドラッグ＆ドロップ</span>
                            <span className="sp">画像をお選びください</span>
                        </p>
                        <img src={url("/images/icon/image_upload_icon.png")}/>
                    </>
                )}
                {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
            </div>
        </div>
    );
}
export default ResizeImageUploader;